import { kbLog } from './keyboardReframeDebug';

// Temporary bottom-padding spacer owned by the inline keyboard guard
// (extracted from inlineKeyboardGuard.js, zero behavior change).

/**
 * `{ scroller, prevPad }` for a temporary bottom-padding spacer we added to
 * manufacture scroll range. Tracked SEPARATELY from `restoreState` because the
 * scroll loan can be handed to `useKeyboardFieldReveal` (see
 * `takeOverInlineReveal`) while the padding stays ours to clean up — otherwise
 * a handed-over reveal would leave the container permanently taller.
 */
let spacerState = null;

/**
 * A HELD spacer is owned by the modal close choreography
 * (`useModalKeyboardAnticipation`): the guard's own delayed restore must not
 * release it (that is the empty gap under scrolled content while the card
 * grows back). Cleared by `releaseSpacer` / a spacer on another scroller.
 */
let spacerHeld = false;

/**
 * KAN-930: spacers PARKED on a "scroll once, stay" scroller (drawers and
 * full-screen flows) while the keyboard is still up somewhere (e.g. a pop-up
 * field over a drawer). A parked spacer is NOT live (`hasLiveInlineSpacer()`
 * is false) so modal height freezes never wait on it.
 * Map(scroller -> the ORIGINAL inline paddingBottom string).
 *
 * Founder feedback 2026-10-22 (after KAN-930 shipped): parking until the drawer
 * closed left a big white area under the last field. A parked spacer now never
 * survives the keyboard close — `inlineKeyboardSettle.js` glides it away.
 * `releaseParkedSpacersWithin` stays as the unmount safety net.
 */
const parked = new Map();

/** scroller -> cancel fn of its running settle glide (inlineKeyboardSettle). */
const settleCancels = new Map();

/**
 * The CSS custom property the spacer mirrors (px of padding WE added). A
 * content child whose `min-height` is a percentage of the scroller (AppShell's
 * pull-to-refresh `calc(100% + 1px)`) shrinks 1:1 with the scroller's padding,
 * so padding alone can never create range on a short page (KAN-930 follow-up,
 * the "header jumps on an empty search" bug) — that child adds this var back.
 */
export const SPACER_VAR = '--cvx-kb-spacer';

/**
 * The scroller's APPLIED bottom padding in px. The inline string can be a
 * `calc()` (Log Job's phone scroller clears its fixed bar with
 * `calc(95px + max(12px, env(safe-area-inset-bottom)))`), which `parseFloat`
 * reads as 0 — the spacer then REPLACED that clearance instead of adding to it,
 * and the keyboard-close settle shrank the padding to 0 and snapped the
 * clearance back at the end (a ~107px dip + jump, 2026-10-22). Plain px reads
 * inline; anything else reads the computed value (inline as a fallback).
 */
export function paddingBottomPx(scroller) {
  const inline = scroller.style.paddingBottom;
  if (/^-?\d*\.?\d+px$/.test(inline)) return parseFloat(inline);
  const cs = typeof window !== 'undefined' && typeof window.getComputedStyle === 'function'
    ? parseFloat(window.getComputedStyle(scroller).paddingBottom) : NaN;
  return Number.isFinite(cs) ? cs : (parseFloat(inline) || 0);
}

/**
 * scroller -> the ORIGINAL padding's px, measured ONCE when the spacer is taken
 * (kept while it is parked). A calc() original reads as its applied px, so the
 * mirrored var holds only what WE added (current − original px), never the
 * whole padding. Cleared when the spacer is released.
 */
const origPxOf = new Map();

/** Write the scroller's padding AND mirror the added px into SPACER_VAR. */
export function writeSpacerPad(scroller, padStr, origStr) {
  scroller.style.paddingBottom = padStr;
  const origPx = origPxOf.has(scroller) ? origPxOf.get(scroller) : (parseFloat(origStr) || 0);
  const added = padStr === origStr ? 0 : (parseFloat(padStr) || 0) - origPx;
  if (typeof scroller.style.setProperty !== 'function') return;
  if (added > 0) scroller.style.setProperty(SPACER_VAR, `${added}px`);
  else scroller.style.removeProperty(SPACER_VAR);
}

function cancelSettle(scroller) {
  const cancel = settleCancels.get(scroller);
  if (cancel) cancel();
  settleCancels.delete(scroller);
}

/** Register / clear the running settle for a parked scroller. */
export function setParkedSettleCancel(scroller, cancel) {
  if (cancel) settleCancels.set(scroller, cancel);
  else settleCancels.delete(scroller);
}

/** True while a settle glide runs on `scroller`. */
export function isParkedSettling(scroller) {
  return settleCancels.has(scroller);
}

/** Stop every running settle (a re-focus); the spacers stay parked, partial. */
export function cancelAllSettles() {
  [...settleCancels.keys()].forEach(cancelSettle);
}

/** Connected parked scrollers. */
export function parkedScrollers() {
  sweepDisconnectedParked();
  return [...parked.keys()];
}

/** The ORIGINAL padding string recorded for a parked scroller. */
export function parkedOriginal(scroller) {
  return parked.get(scroller);
}

/** True while any spacer is parked. */
export function hasParkedSpacers() {
  return parked.size > 0;
}

/** Put a parked scroller's padding back and forget it. */
export function releaseParkedSpacer(scroller) {
  if (!parked.has(scroller)) return;
  cancelSettle(scroller);
  writeSpacerPad(scroller, parked.get(scroller), parked.get(scroller));
  parked.delete(scroller);
  if (!spacerState || spacerState.scroller !== scroller) origPxOf.delete(scroller);
  kbLog('inlineRevealParkedRelease', {});
}

/** Forget parked scrollers that left the document (a host that unmounted without releasing). */
function sweepDisconnectedParked() {
  [...parked.keys()].forEach((n) => {
    if (!n.isConnected) { cancelSettle(n); parked.delete(n); origPxOf.delete(n); }
  });
}

/** Move the live spacer to the parked set, padding left in place. No-op if none. */
export function parkSpacer() {
  sweepDisconnectedParked();
  const s = spacerState;
  if (!s) return;
  spacerState = null;
  spacerHeld = false;
  // Keep the EARLIEST original if this scroller was parked before.
  if (!parked.has(s.scroller)) parked.set(s.scroller, s.prevPad);
  kbLog('inlineRevealSpacerPark', {});
}

/**
 * Release every parked (and live) spacer inside `root`, plus any whose node has
 * left the document. Called by the drawer / full-screen shells on unmount.
 */
export function releaseParkedSpacersWithin(root) {
  const gone = (n) => !n.isConnected || (root && root.contains(n));
  [...parked.keys()].forEach((n) => { if (gone(n)) releaseParkedSpacer(n); });
  if (spacerState && gone(spacerState.scroller)) releaseSpacer();
}

/** Release every parked spacer (guard teardown / tests). */
export function releaseAllParkedSpacers() {
  [...parked.keys()].forEach(releaseParkedSpacer);
}

/** True while `scroller` holds a parked spacer (tests / diagnostics). */
export function hasParkedSpacer(scroller) {
  return parked.has(scroller);
}

/** Put a spacer'd scroller's padding back exactly as it was. Idempotent. */
export function releaseSpacer() {
  const s = spacerState;
  if (!s) return;
  spacerState = null;
  spacerHeld = false;
  writeSpacerPad(s.scroller, s.prevPad, s.prevPad);
  if (!parked.has(s.scroller)) origPxOf.delete(s.scroller);
  kbLog('inlineRevealSpacerRelease', {});
}

/**
 * Add `px` of temporary bottom padding to `scroller`, recording the original
 * inline value ONCE (first add per scroller) so the restore puts back the
 * exact pre-guard string. Accumulates across chained reveals on the same
 * scroller — each plan re-reads the live scroll range, so later spacers are
 * true additional need, never double-counting.
 */
export function addSpacerPadding(scroller, px) {
  sweepDisconnectedParked();
  if (!spacerState || spacerState.scroller !== scroller) {
    // A parked spacer is reclaimed with its ORIGINAL padding string, so the
    // eventual release puts back what React wrote, not our grown value.
    // A re-focus mid-settle stops the glide where it is (padding partial).
    const reclaimed = parked.has(scroller);
    if (reclaimed) cancelSettle(scroller);
    spacerState = { scroller, prevPad: reclaimed ? parked.get(scroller) : scroller.style.paddingBottom };
    // The original's px, once per take (a reclaimed parked spacer keeps its own).
    if (!reclaimed || !origPxOf.has(scroller)) origPxOf.set(scroller, paddingBottomPx(scroller));
    spacerState.origPx = origPxOf.get(scroller);
    if (reclaimed) parked.delete(scroller);
    spacerHeld = false;
  }
  const cur = paddingBottomPx(scroller); // a calc() original keeps its px (never replaced)
  writeSpacerPad(scroller, `${cur + px}px`, spacerState.prevPad);
}

/** Shave overshoot off the spacer; the tracked original padding is untouched. */
export function trimSpacerPadding(scroller, px) {
  const cur = parseFloat(scroller.style.paddingBottom) || 0;
  const orig = spacerState && spacerState.scroller === scroller ? spacerState.prevPad : '';
  writeSpacerPad(scroller, `${Math.max(0, cur - px)}px`, orig);
}

/** True while the guard holds a spacer (padding it added and has not yet released). */
export function hasLiveInlineSpacer() {
  return spacerState != null;
}

/** The scroller the live spacer sits on, or null. */
export function inlineSpacerScroller() {
  return spacerState ? spacerState.scroller : null;
}

/** Mark the live spacer on `scroller` as held by another owner. False if none there. */
export function holdInlineSpacer(scroller) {
  if (!spacerState || spacerState.scroller !== scroller) return false;
  spacerHeld = true;
  return true;
}

/** True while another owner holds the spacer — the guard must not release it. */
export function isInlineSpacerHeld() {
  return spacerState != null && spacerHeld;
}

/**
 * Run a synchronous measurement with the spacer's padding temporarily removed
 * (no paint in between), so a content-hugging box reads its NATURAL size.
 */
export function measureWithoutSpacer(fn) {
  const s = spacerState;
  if (!s) return fn();
  const cur = s.scroller.style.paddingBottom;
  writeSpacerPad(s.scroller, s.prevPad, s.prevPad);
  try {
    return fn();
  } finally {
    writeSpacerPad(s.scroller, cur, s.prevPad);
  }
}
